<template>
  <div>
    <!-- CABECERA Y FILTROS PRINCIPALES -->
    <CRow class="mb-4 align-items-center">
      <CCol lg="6" md="12" class="mb-2 mb-lg-0">
        <h4 class="mb-0 text-primary font-weight-bold">
          <i class="fas fa-chart-pie"></i> Estadísticas del Hotel
        </h4>
        <p class="text-muted small mb-0">Rendimiento financiero e inventario.</p>
      </CCol>

      <CCol lg="6" md="12" class="text-right">
        <div class="d-flex justify-content-end align-items-center flex-wrap gap-2">
          <select class="form-control form-control-sm font-weight-bold shadow-sm" style="width: 220px;" v-model="filters.department_id" @change="applyAllFilters">
            <option value="">TODOS LOS DEPARTAMENTOS</option>
            <option v-for="dept in departments" :key="dept.id" :value="dept.id">{{ dept.name }}</option>
          </select>
        </div>
      </CCol>
    </CRow>

    <!-- FILTROS DE FECHA PARA REPORTES -->
    <CRow class="mb-4">
      <CCol lg="3" md="6" class="mb-2">
        <label class="small font-weight-bold text-muted">Fecha Desde</label>
        <input type="date" class="form-control form-control-sm" v-model="filters.date_from" @change="applyAllFilters">
      </CCol>
      <CCol lg="3" md="6" class="mb-2">
        <label class="small font-weight-bold text-muted">Fecha Hasta</label>
        <input type="date" class="form-control form-control-sm" v-model="filters.date_to" @change="applyAllFilters">
      </CCol>
      <CCol lg="2" md="6" class="mb-2 d-flex align-items-end">
        <button class="btn btn-sm btn-info font-weight-bold" @click="applyAllFilters">
          <i class="fas fa-search"></i> Aplicar Filtros
        </button>
      </CCol>
      <CCol lg="4" md="6" class="mb-2 d-flex align-items-end justify-content-end">
        <div class="text-muted small">
          <i class="fas fa-info-circle"></i> Seleccione un rango de fechas para ver los reportes.
        </div>
      </CCol>
    </CRow>

    <!-- TARJETAS DE RESUMEN FINANCIERO -->
    <CRow class="mb-4 text-center">
      <CCol md="3" sm="6" class="mb-3">
        <CCard class="shadow-sm border-success h-100">
          <CCardBody class="p-3 d-flex flex-column justify-content-center">
            <div class="text-muted small text-uppercase font-weight-bold mb-1">
              <i class="fas fa-hand-holding-usd text-success"></i> Ingresos (Hospedaje)
            </div>
            <h4 class="text-success mb-0 font-weight-bold">S/ {{ parseFloat(totals.income).toFixed(2) }}</h4>
          </CCardBody>
        </CCard>
      </CCol>
      <CCol md="3" sm="6" class="mb-3">
        <CCard class="shadow-sm border-danger h-100">
          <CCardBody class="p-3 d-flex flex-column justify-content-center">
            <div class="text-muted small text-uppercase font-weight-bold mb-1">
              <i class="fas fa-receipt text-danger"></i> Egresos Operativos
            </div>
            <h4 class="text-danger mb-0 font-weight-bold">S/ {{ parseFloat(totals.expense).toFixed(2) }}</h4>
          </CCardBody>
        </CCard>
      </CCol>
      <CCol md="3" sm="6" class="mb-3">
        <CCard class="shadow-sm h-100" :class="totals.profit >= 0 ? 'bg-primary text-white' : 'bg-warning text-dark'">
          <CCardBody class="p-3 d-flex flex-column justify-content-center">
            <div class="small text-uppercase font-weight-bold mb-1">
              <i class="fas fa-chart-line"></i> Utilidad Neta
            </div>
            <h3 class="mb-0 font-weight-bold">S/ {{ parseFloat(totals.profit).toFixed(2) }}</h3>
          </CCardBody>
        </CCard>
      </CCol>
      <CCol md="3" sm="6" class="mb-3">
        <CCard class="shadow-sm border-info h-100">
          <CCardBody class="p-3 d-flex flex-column justify-content-center">
            <div class="text-muted small text-uppercase font-weight-bold mb-1">
              <i class="fas fa-boxes text-info"></i> Inversión Inventario
            </div>
            <h4 class="text-info mb-0 font-weight-bold">S/ {{ parseFloat(totals.inventory_cost).toFixed(2) }}</h4>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- GRÁFICOS PRINCIPALES -->
    <CRow>
      <!-- Gráfico de Barras - Flujo de Caja Mensual -->
      <CCol lg="8" class="mb-4">
        <CCard class="shadow-sm border-0 h-100">
          <CCardHeader class="bg-dark text-white font-weight-bold">
            <i class="fas fa-chart-bar"></i> Flujo de Caja (Evolución Mensual)
          </CCardHeader>
          <CCardBody>
            <div v-if="isLoading" class="text-center py-5 text-muted">
              <i class="fas fa-spinner fa-spin fa-2x"></i>
            </div>
            <CChartBar
              v-else-if="chartLabels.length > 0"
              :datasets="barChartData"
              :options="barChartOptions"
              :labels="chartLabels"
              style="height: 300px;"
            />
            <div v-else class="text-muted font-weight-bold text-center py-5">
               No hay suficientes datos para graficar el flujo de caja.
            </div>
          </CCardBody>
        </CCard>
      </CCol>

      <!-- Gráfico Circular - Ingresos por Departamento -->
      <CCol lg="4" class="mb-4">
        <CCard class="shadow-sm border-0 h-100">
          <CCardHeader class="bg-dark text-white font-weight-bold">
            <i class="fas fa-chart-pie"></i> Ingresos por Departamento
          </CCardHeader>
          <CCardBody class="d-flex align-items-center justify-content-center">
            <div v-if="isLoading" class="text-center py-5 text-muted">
              <i class="fas fa-spinner fa-spin fa-2x"></i>
            </div>
            <div v-else-if="pieData.length == 0 || totals.income == 0" class="text-muted font-weight-bold text-center py-5">
               Información por departamento no disponible en este periodo.
            </div>
            <CChartPie
              v-else
              :datasets="pieChartData"
              :labels="pieLabels"
              :options="{ responsive: true, maintainAspectRatio: false }"
              style="height: 250px; width: 100%;"
            />
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- VENTAS Y COMPRAS MENSUALES -->
    <CRow class="mb-4">
      <CCol lg="6" class="mb-4">
        <CCard class="shadow-sm border-0 h-100">
          <CCardHeader class="bg-success text-white font-weight-bold">
            <i class="fas fa-chart-line"></i> Ventas Mensuales (Evolución)
          </CCardHeader>
          <CCardBody>
            <div v-if="loadingSales" class="text-center py-5 text-muted">
              <i class="fas fa-spinner fa-spin fa-2x"></i>
            </div>
            <div v-else-if="monthlySalesData.length == 0" class="text-muted font-weight-bold text-center py-5">
              No hay datos de ventas en el rango seleccionado.
            </div>
            <div v-else>
              <CChartBar
                :datasets="monthlySalesChartData"
                :labels="monthlySalesLabels"
                :options="monthlyChartOptions"
                style="height: 300px;"
              />
            </div>
          </CCardBody>
        </CCard>
      </CCol>

      <CCol lg="6" class="mb-4">
        <CCard class="shadow-sm border-0 h-100">
          <CCardHeader class="bg-warning text-dark font-weight-bold">
            <i class="fas fa-chart-line"></i> Compras Mensuales (Gastos Operativos)
          </CCardHeader>
          <CCardBody>
            <div v-if="loadingPurchases" class="text-center py-5 text-muted">
              <i class="fas fa-spinner fa-spin fa-2x"></i>
            </div>
            <div v-else-if="monthlyPurchasesData.length == 0" class="text-muted font-weight-bold text-center py-5">
              No hay datos de compras en el rango seleccionado.
            </div>
            <div v-else>
              <CChartBar
                :datasets="monthlyPurchasesChartData"
                :labels="monthlyPurchasesLabels"
                :options="monthlyChartOptions"
                style="height: 300px;"
              />
            </div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- RANKING DE CLIENTES POTENCIALES -->
    <CRow>
      <CCol md="12" class="mb-4">
        <CCard class="shadow-sm border-0">
          <CCardHeader class="bg-primary text-white font-weight-bold">
            <i class="fas fa-trophy"></i> Ranking de Clientes Potenciales (Más estadías)
          </CCardHeader>
          <CCardBody>
            <div v-if="loadingRanking" class="text-center py-5 text-muted">
              <i class="fas fa-spinner fa-spin fa-2x"></i>
            </div>
            <div v-else-if="rankingClients.length == 0" class="text-muted font-weight-bold text-center py-5">
              No hay datos de clientes en el rango seleccionado.
            </div>
            <div v-else class="table-responsive">
              <table class="table table-hover table-striped text-center align-middle mb-0">
                <thead class="bg-dark text-white">
                  <tr>
                    <th>#</th>
                    <th class="text-left">Cliente</th>
                    <th>Documento</th>
                    <th>Total Estadías</th>
                    <th>Total Días Hospedados</th>
                    <th>Total Gastado (S/)</th>
                    <th>Última Visita</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(client, index) in rankingClients" :key="client.client_document || index">
                    <td class="font-weight-bold">
                      {{ index + 1 }}
                      <span v-if="index < 3" class="ml-1">
                        <i v-if="index == 0" class="fas fa-crown text-warning"></i>
                        <i v-else-if="index == 1" class="fas fa-medal text-secondary"></i>
                        <i v-else-if="index == 2" class="fas fa-medal" style="color: #cd7f32;"></i>
                      </span>
                    </td>
                    <td class="text-left font-weight-bold">{{ client.client_name }}</td>
                    <td>{{ client.client_document }}</td>
                    <td class="font-weight-bold text-primary">{{ client.total_checkins }}</td>
                    <td class="font-weight-bold text-info">{{ client.total_days }}</td>
                    <td class="font-weight-bold text-success">S/ {{ parseFloat(client.total_spent).toFixed(2) }}</td>
                    <td><small>{{ formatShortDate(client.last_visit) }}</small></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { CChartBar, CChartPie } from "@coreui/vue-chartjs";
import moment from "moment";

export default {
  name: "HotelDashboard",
  components: {
    CChartBar,
    CChartPie
  },
  data() {
    return {
      isLoading: false,
      loadingSales: false,
      loadingPurchases: false,
      loadingRanking: false,
      departments: [],

      filters: {
        department_id: "",
        date_from: moment().subtract(6, 'months').format("YYYY-MM-DD"),
        date_to: moment().format("YYYY-MM-DD")
      },

      totals: {
        income: 0,
        expense: 0,
        profit: 0,
        inventory_cost: 0
      },

      // Gráficos principales
      chartLabels: [],
      incomeData: [],
      expenseData: [],
      pieLabels: [],
      pieData: [],

      // Reportes avanzados
      monthlySalesData: [],
      monthlySalesLabelsArray: [],
      monthlyPurchasesData: [],
      monthlyPurchasesLabelsArray: [],

      // Ranking
      rankingClients: [],

      barChartOptions: {
        maintainAspectRatio: false,
        responsive: true,
        tooltips: {
          callbacks: { label: function(t) { return 'S/ ' + t.yLabel.toFixed(2); } }
        },
        scales: {
          yAxes: [{ ticks: { beginAtZero: true } }]
        }
      },

      monthlyChartOptions: {
        maintainAspectRatio: false,
        responsive: true,
        tooltips: {
          callbacks: { label: function(t) { return 'S/ ' + t.yLabel.toFixed(2); } }
        },
        scales: {
          yAxes: [{ ticks: { beginAtZero: true } }]
        }
      }
    };
  },
  computed: {
    barChartData() {
      return [
        { label: 'Ingresos (S/)', backgroundColor: '#2eb85c', data: this.incomeData },
        { label: 'Egresos (S/)', backgroundColor: '#e55353', data: this.expenseData }
      ];
    },
    pieChartData() {
      return [{ backgroundColor: ['#321fdb', '#f9b115', '#e55353', '#3399ff', '#2eb85c', '#ced2d8', '#8a93a2', '#4f5d73'], data: this.pieData }];
    },
    monthlySalesChartData() {
      return [{ label: 'Ventas Mensuales (S/)', backgroundColor: '#2eb85c', data: this.monthlySalesData }];
    },
    monthlySalesLabels() {
      return this.monthlySalesLabelsArray;
    },
    monthlyPurchasesChartData() {
      return [{ label: 'Gastos Operativos (S/)', backgroundColor: '#e55353', data: this.monthlyPurchasesData }];
    },
    monthlyPurchasesLabels() {
      return this.monthlyPurchasesLabelsArray;
    }
  },
  created() {
    this.loadDepartments();
    this.applyAllFilters();
  },
  methods: {
    ...mapActions(["get", "post"]),

    formatShortDate(date) {
      if (!date) return '-';
      return moment(date).format("DD/MM/YYYY");
    },

    loadDepartments() {
      this.get({
        url: `${this.$store.getters.get__url}/hotel/departments`,
        token: this.$store.getters.get__token,
      }).then((res) => {
        if (res.success || res.statusCode == 200) {
          this.departments = res.data;
        } else if (Array.isArray(res)) {
          this.departments = res;
        }
      });
    },

    // Aplica todos los filtros
    applyAllFilters() {
      this.fetchDashboardData();
      this.fetchMonthlySales();
      this.fetchMonthlyPurchases();
      this.fetchRankingData();
    },

    fetchDashboardData() {
      this.isLoading = true;
      this.post({
        url: `${this.$store.getters.get__url}/hotel/dashboard-range`,
        token: this.$store.getters.get__token,
        params: {
          date_from: this.filters.date_from,
          date_to: this.filters.date_to,
          department_id: this.filters.department_id
        }
      })
      .then(res => {
        let data = res.data || {};
        let t = data.totals || {};

        this.totals = {
          income: Number(t.income) || 0,
          expense: Number(t.expenses) || 0,
          profit: Number(t.profit) || 0,
          inventory_cost: Number(t.inventory_cost) || 0
        };

        if (data.monthly && Array.isArray(data.monthly)) {
            this.chartLabels = data.monthly.map(m => m.month_name || '-');
            this.incomeData = data.monthly.map(m => Number(m.income) || 0);
            this.expenseData = data.monthly.map(m => Number(m.expense) || 0);
        } else {
            this.chartLabels = [];
            this.incomeData = [];
            this.expenseData = [];
        }

        this.pieLabels = data.pie_labels || [];
        this.pieData = data.pie_data || [];
      })
      .catch(err => {
        console.error("Error al cargar dashboard:", err);
      })
      .finally(() => {
        this.isLoading = false;
      });
    },

    // Ventas mensuales (evolución)
    fetchMonthlySales() {
      this.loadingSales = true;
      this.post({
        url: `${this.$store.getters.get__url}/hotel/monthly-sales`,
        token: this.$store.getters.get__token,
        params: {
          date_from: this.filters.date_from,
          date_to: this.filters.date_to,
          department_id: this.filters.department_id
        }
      })
      .then(res => {
        this.monthlySalesData = res.data.map(item => item.total) || [];
        this.monthlySalesLabelsArray = res.data.map(item => item.month_name) || [];
      })
      .catch(err => {
        console.error("Error al cargar ventas mensuales:", err);
        this.monthlySalesData = [];
        this.monthlySalesLabelsArray = [];
      })
      .finally(() => {
        this.loadingSales = false;
      });
    },

    // Compras mensuales (gastos operativos)
    fetchMonthlyPurchases() {
      this.loadingPurchases = true;
      this.post({
        url: `${this.$store.getters.get__url}/hotel/monthly-purchases`,
        token: this.$store.getters.get__token,
        params: {
          date_from: this.filters.date_from,
          date_to: this.filters.date_to,
          department_id: this.filters.department_id
        }
      })
      .then(res => {
        this.monthlyPurchasesData = res.data.map(item => item.total) || [];
        this.monthlyPurchasesLabelsArray = res.data.map(item => item.month_name) || [];
      })
      .catch(err => {
        console.error("Error al cargar compras mensuales:", err);
        this.monthlyPurchasesData = [];
        this.monthlyPurchasesLabelsArray = [];
      })
      .finally(() => {
        this.loadingPurchases = false;
      });
    },

    // Ranking de clientes (todos los que tengan estadías o vouchers)
    fetchRankingData() {
      this.loadingRanking = true;
      this.post({
        url: `${this.$store.getters.get__url}/hotel/client-ranking`,
        token: this.$store.getters.get__token,
        params: {
          date_from: this.filters.date_from,
          date_to: this.filters.date_to
        }
      })
      .then(res => {
        this.rankingClients = res.data || [];
      })
      .catch(err => {
        console.error("Error al cargar ranking de clientes:", err);
        this.rankingClients = [];
      })
      .finally(() => {
        this.loadingRanking = false;
      });
    }
  }
};
</script>

<style scoped>
.gap-2 { gap: 0.5rem !important; }
</style>